import { useState } from "react";
import { Mail, Phone, Send, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EditableImage } from "@/components/EditableImage";
import { EditableText } from "@/components/EditableText";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const DEFAULT_IMG = "https://images.pexels.com/photos/15281444/pexels-photo-15281444.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const REASONS = [
  { value: "prova_gratuita", label: "Vorrei fare una prova gratuita" },
  { value: "domanda_generica", label: "Vorrei fare una domanda generica" },
];

export function ContactSection() {
  const empty = { first_name: "", last_name: "", email: "", email_confirm: "", subject: "", reason: "", message: "" };
  const [form, setForm] = useState(empty);
  const [sending, setSending] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim()) { toast.error("Inserisci il nome"); return; }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) { toast.error("Inserisci un'email valida"); return; }
    if (form.email !== form.email_confirm) { toast.error("Le due email non coincidono"); return; }
    if (!form.reason) { toast.error("Seleziona la ragione della richiesta"); return; }
    if (!form.message.trim()) { toast.error("Scrivi un messaggio"); return; }
    setSending(true);
    try {
      await api.post("/contact", {
        first_name: form.first_name, last_name: form.last_name, email: form.email,
        subject: form.subject, reason: form.reason, message: form.message,
      });
      toast.success("Messaggio inviato! Ti risponderemo al più presto.");
      setForm(empty);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore nell'invio");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-8 items-start" data-testid="contact-section">
      {/* Colonna sinistra: immagine + info */}
      <div className="rounded-2xl border border-white/10 overflow-hidden bg-[#0B1220]/40">
        <EditableImage slot="contatti_image" defaultSrc={DEFAULT_IMG} alt="Contatti" className="w-full h-64" imgClassName="w-full h-64 object-cover" />
        <div className="p-7 space-y-5">
          <h2 className="font-display text-2xl md:text-3xl tracking-tight">
            <EditableText slot="contatti_title" defaultText="Contatti Settore Nazionale Ginnastiche" />
          </h2>
          <div className="space-y-4 text-sm">
            <div>
              <p className="text-gold font-semibold"><EditableText slot="contatti_artistica_role" defaultText="Responsabile nazionale ginnastica artistica" /></p>
              <p className="text-foreground mt-0.5"><EditableText slot="contatti_artistica_name" defaultText="Catia Desti" /></p>
              <p className="text-muted-foreground inline-flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> <EditableText slot="contatti_artistica_phone" defaultText="380 863 7541" /></p>
            </div>
            <div>
              <p className="text-gold font-semibold"><EditableText slot="contatti_ritmica_role" defaultText="Responsabile nazionale ginnastica ritmica" /></p>
              <p className="text-foreground mt-0.5"><EditableText slot="contatti_ritmica_name" defaultText="Anna Ianzini" /></p>
              <p className="text-muted-foreground inline-flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> <EditableText slot="contatti_ritmica_phone" defaultText="331 256 3990" /></p>
            </div>
            <div>
              <p className="text-gold font-semibold">Email</p>
              <p className="text-muted-foreground inline-flex items-center gap-1.5 break-all"><Mail className="w-3.5 h-3.5" /> <EditableText slot="contatti_email" defaultText="settoreopesginnastiche@gmail.com" /></p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground pt-3 border-t border-white/10">
            <EditableText slot="contatti_copyright" defaultText="© 2025 Settore Nazionale Opes Ginnastiche – Tutti i diritti riservati" />
          </p>
        </div>
      </div>

      {/* Colonna destra: form */}
      <form onSubmit={submit} className="rounded-2xl border border-gold/20 bg-gradient-to-b from-gold/5 to-transparent p-7 space-y-4" data-testid="contact-form">
        <h3 className="font-display text-2xl">Contattaci</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Nome</Label><Input data-testid="contact-first-name" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} className="mt-1.5" /></div>
          <div><Label>Cognome</Label><Input data-testid="contact-last-name" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} className="mt-1.5" /></div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Email</Label><Input data-testid="contact-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5" /></div>
          <div><Label>Conferma Email</Label><Input data-testid="contact-email-confirm" type="email" value={form.email_confirm} onChange={(e) => set("email_confirm", e.target.value)} className="mt-1.5" /></div>
        </div>
        <div><Label>Oggetto</Label><Input data-testid="contact-subject" value={form.subject} onChange={(e) => set("subject", e.target.value)} className="mt-1.5" /></div>
        <div>
          <Label>Ragione della richiesta *</Label>
          <Select value={form.reason} onValueChange={(v) => set("reason", v)}>
            <SelectTrigger data-testid="contact-reason" className="mt-1.5"><SelectValue placeholder="Seleziona un'opzione" /></SelectTrigger>
            <SelectContent>
              {REASONS.map((r) => <SelectItem key={r.value} value={r.value} data-testid={`contact-reason-${r.value}`}>{r.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div><Label>Messaggio *</Label><Textarea data-testid="contact-message" value={form.message} onChange={(e) => set("message", e.target.value)} rows={5} className="mt-1.5" /></div>
        <Button type="submit" data-testid="contact-submit-btn" disabled={sending} className="w-full bg-gold text-[#020617] hover:bg-[#FDE047] font-semibold">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1.5" /> Invia il tuo messaggio</>}
        </Button>
      </form>
    </div>
  );
}
